import React from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { FILTERS } from "@/data/agreements/categories";
import { STAGES, IMPORTANCE } from "@/data/agreements/stages";

/** Radix Select cannot hold an empty value, so "all" is the on-the-wire sentinel for "no filter". */
const ALL = "all";

/**
 * FilterBar — the library's controls. Fully controlled: it holds no state of its own.
 *
 * props
 *   query, onQuery              search string + setter
 *   filters, onFilters          string[] of FILTERS chips + setter (multi-select, toggling)
 *   stage, onStage              STAGE id or "" + setter (receives "" when cleared)
 *   importance, onImportance    IMPORTANCE id or "" + setter (receives "" when cleared)
 *   resultCount                 number shown beside the controls; omit to hide
 *   onClear                     optional; when absent the bar clears each control itself
 */
export default function FilterBar({
  query = "",
  onQuery,
  filters,
  onFilters,
  stage = "",
  onStage,
  importance = "",
  onImportance,
  resultCount,
  onClear,
  className,
}) {
  const active = Array.isArray(filters) ? filters : [];
  const hasActive = Boolean(query) || active.length > 0 || Boolean(stage) || Boolean(importance);

  const toggle = (chip) => {
    if (!onFilters) return;
    onFilters(active.includes(chip) ? active.filter((f) => f !== chip) : [...active, chip]);
  };

  const clearAll = () => {
    if (onClear) {
      onClear();
      return;
    }
    onQuery?.("");
    onFilters?.([]);
    onStage?.("");
    onImportance?.("");
  };

  const countLabel =
    typeof resultCount === "number"
      ? resultCount === 0
        ? "No documents"
        : `${resultCount} document${resultCount === 1 ? "" : "s"}`
      : null;

  return (
    <div className={cn("space-y-3", className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Label htmlFor="agreements-search" className="sr-only">
          Search documents
        </Label>
        <Input
          id="agreements-search"
          type="search"
          value={query}
          onChange={(e) => onQuery?.(e.target.value)}
          placeholder="Search agreements, e.g. co-packer, distributor, NDA"
          className="pl-9"
        />
      </div>

      {/* Chips scroll horizontally rather than wrapping into a wall on a phone. */}
      <div className="-mx-1 overflow-x-auto px-1 pb-1" role="group" aria-label="Filter by relationship">
        <div className="flex w-max gap-1.5">
          {FILTERS.map((chip) => {
            const on = active.includes(chip);
            return (
              <button
                key={chip}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(chip)}
                className={cn(
                  "shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  on
                    ? "border-indigo-300 bg-indigo-600 text-white hover:bg-indigo-600/90"
                    : "border-input bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {chip}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="grid grid-cols-2 gap-2 sm:w-auto">
          <div className="min-w-0">
            <Label htmlFor="agreements-stage" className="sr-only">
              Business stage
            </Label>
            <Select value={stage || ALL} onValueChange={(v) => onStage?.(v === ALL ? "" : v)}>
              <SelectTrigger id="agreements-stage" className="sm:w-[190px]">
                <SelectValue placeholder="Any stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any stage</SelectItem>
                {STAGES.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-0">
            <Label htmlFor="agreements-importance" className="sr-only">
              Importance
            </Label>
            <Select value={importance || ALL} onValueChange={(v) => onImportance?.(v === ALL ? "" : v)}>
              <SelectTrigger id="agreements-importance" className="sm:w-[190px]">
                <SelectValue placeholder="Any importance" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any importance</SelectItem>
                {IMPORTANCE.map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 sm:ml-auto">
          {countLabel && (
            <span className="text-sm text-muted-foreground" aria-live="polite">
              {countLabel}
            </span>
          )}
          {hasActive && (
            <Button type="button" variant="ghost" size="sm" onClick={clearAll} className="shrink-0 text-muted-foreground">
              <X className="mr-1 h-3.5 w-3.5" /> Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
